<template>
  <div class="create-container">
    <AppHeader title="Create New Device" back-to="/" back-text="Back to Devices" />

    <main class="main-content">
      <div class="device-form">
        <div class="form-grid">
          <div class="form-group">
            <label>Device ID:</label>
            <input v-model="newDevice.id" type="text" placeholder="TAG-1234" pattern="TAG-\d+" />
            <span v-if="newDevice.id && !isValidTag(newDevice.id)" class="error-text">ID must be TAG-{number}</span>
          </div>
          <div class="form-group">
            <label>Model:</label>
            <input v-model="newDevice.model" type="text" placeholder="iPhone 14 Pro" />
          </div>
          <div class="form-group">
            <label>PIN:</label>
            <input v-model="newDevice.pin" type="text" placeholder="000001" />
          </div>
          <div class="form-group">
            <label>Image URL:</label>
            <input v-model="newDevice.imageCdnPath" type="text" placeholder="https://..." />
          </div>
          <div class="form-group">
            <label>Type:</label>
            <select v-model="newDevice.type">
              <option value="smartphone">Smartphone</option>
              <option value="tablet">Tablet</option>
            </select>
          </div>
          <div class="form-group">
            <label>Model ID:</label>
            <input v-model="newDevice.modelId" type="text" placeholder="A2894" />
          </div>
          <div class="form-group">
            <label>OS:</label>
            <select v-model="newDevice.os">
              <option value="iOS">iOS</option>
              <option value="Android">Android</option>
              <option value="iPadOS">iPadOS</option>
              <option value="Windows">Windows</option>
            </select>
          </div>
          <div class="form-group">
            <label>OS Version:</label>
            <input v-model="newDevice.osVersion" type="text" placeholder="16.4" />
          </div>
          <div class="form-group">
            <label>RAM (GB):</label>
            <input v-model="newDevice.ram" type="number" step="1" placeholder="8" />
          </div>
          <div class="form-group">
            <label>Team:</label>
            <select v-model="newDevice.team">
              <option value="" disabled>Select Team</option>
              <option value="Top Eleven">Top Eleven</option>
              <option value="Golf Rival">Golf Rival</option>
              <option value="Top Goal">Top Goal</option>
            </select>
          </div>
          <div class="form-group">
            <label>Owner:</label>
            <select v-model="newDevice.owner">
              <option value="" disabled>Select Owner</option>
              <option v-for="admin in adminUsers" :key="admin.email" :value="admin.email">
                {{ admin.name || admin.email }}
              </option>
            </select>
          </div>
        </div>

        <div class="actions">
          <button @click="createDevice" class="create-btn" :disabled="!canCreate">Create Device</button>
          <button @click="resetForm" class="reset-btn">Reset Form</button>
        </div>
      </div>
    </main>

    <CustomPopup
      :show="popup.show"
      :type="popup.type"
      :title="popup.title"
      :message="popup.message"
      @close="closePopup"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { addDevice } from '../services/deviceService'
import { getUserRole, getAdminUsers } from '../services/userService'
import { COLORS, FONTS } from '@/styles/brand'
import CustomPopup from './CustomPopup.vue'
import AppHeader from './AppHeader.vue'

const router = useRouter()
const currentUser = ref(null)
const userRole = ref('user')
const popup = ref({ show: false, type: '', title: '', message: '' })
const adminUsers = ref([])

const newDevice = ref({
  id: '',
  model: '',
  pin: '',
  imageCdnPath: '',
  type: 'smartphone',
  modelId: '',
  os: 'iOS',
  osVersion: '',
  ram: null,
  team: '',
  owner: '',
  lastTakenBy: null,
  lastTakenAt: null,
  totalHours: 0,
  checkoutCount: 0
})

const isValidTag = (id) => {
  return /^TAG-\d+$/.test(id)
}

const canCreate = computed(() => {
  return newDevice.value.id && newDevice.value.model && newDevice.value.pin && isValidTag(newDevice.value.id)
})

const createDevice = async () => {
  try {
    await addDevice(newDevice.value.id, newDevice.value)
    showPopup('success', 'Success', 'Device created successfully!')
    setTimeout(() => {
      router.push('/')
    }, 2000)
  } catch (error) {
    console.error('Error creating device:', error)
    showPopup('error', 'Error', 'Failed to create device')
  }
}

const resetForm = () => {
  newDevice.value = {
    id: '',
    model: '',
    pin: '',
    imageCdnPath: '',
    type: 'smartphone',
    modelId: '',
    os: 'iOS',
    osVersion: '',
    ram: null,
    team: '',
    owner: '',
    lastTakenBy: null,
    lastTakenAt: null,
    totalHours: 0,
    checkoutCount: 0
  }
}

const showPopup = (type, title, message) => {
  popup.value = { show: true, type, title, message }
}

const closePopup = () => {
  popup.value.show = false
}

onMounted(async () => {
  adminUsers.value = await getAdminUsers()
  
  onAuthStateChanged(auth, async (user) => {
    if (user && user.email.endsWith('@nordeus.com')) {
      currentUser.value = user
      userRole.value = await getUserRole(user.email)

      if (userRole.value !== 'admin') {
        router.push('/')
      }
    } else {
      router.push('/')
    }
  })
})
</script>

<style scoped>
.create-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%),
    url('@/assets/nordeus-3840x1080-pattern_new.jpg') no-repeat center center fixed;
  background-size: cover;
  position: relative;
  font-family: v-bind('FONTS.GOOGLE');
}

.main-content {
  flex: 1;
  padding: 40px;
}

.device-form {
  max-width: 800px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-weight: 600;
  margin-bottom: 8px;
}

.form-group input, .form-group select {
  padding: 12px 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: v-bind('COLORS.PRIMARY.WHITE');
  font-size: 1rem;
}

.form-group input::placeholder {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 200;
}

.form-group input:focus, .form-group select:focus {
  outline: none;
  border-color: v-bind('COLORS.PRIMARY.RED');
}

.actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.create-btn, .reset-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.create-btn {
  background: v-bind('COLORS.PRIMARY.RED');
  color: white;
}

.create-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reset-btn {
  background: rgba(255, 255, 255, 0.2);
  color: v-bind('COLORS.PRIMARY.WHITE');
}

.create-btn:hover:not(:disabled), .reset-btn:hover {
  transform: translateY(-2px);
}

.error-text {
  color: v-bind('COLORS.PRIMARY.RED');
  font-size: 0.85rem;
  margin-top: 4px;
}
</style>
